import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowLeft, Check, PackageCheck, SlidersHorizontal } from "lucide-react";

import { CatalogoCard } from "@/components/site/CatalogoCard";
import { Footer } from "@/components/site/Footer";
import { Header } from "@/components/site/Header";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CATEGORIAS_PRODUCTO, productos, type CategoriaProducto } from "@/lib/productos";

type CategoriaFiltro = CategoriaProducto | "todos";
type DisponibilidadFiltro = "todos" | "disponible" | "a-pedido";
type PrecioFiltro = "todos" | "hasta-10000" | "10001-20000" | "mas-20000";
type OrdenFiltro = "destacados" | "precio-asc" | "precio-desc" | "nombre";

const categoriasValidas = new Set<CategoriaFiltro>([
  "todos",
  ...CATEGORIAS_PRODUCTO.map((categoria) => categoria.id),
]);

export const Route = createFileRoute("/productos")({
  validateSearch: (search: Record<string, unknown>): { categoria?: CategoriaFiltro } => {
    const categoria = search["categoria"];
    return typeof categoria === "string" &&
      categoria !== "todos" &&
      categoriasValidas.has(categoria as CategoriaFiltro)
      ? { categoria: categoria as CategoriaFiltro }
      : {};
  },
  component: ProductosPage,
  head: () => ({
    meta: [
      { title: "Stands NFC + QR, tarjetas NFC y llaveros | Pyme Visible" },
      {
        name: "description",
        content:
          "Compra stands NFC + QR, tarjetas NFC, llaveros y productos personalizados para negocios en Chile. Configurados y listos para usar.",
      },
      { property: "og:title", content: "Stands NFC + QR, tarjetas NFC y llaveros" },
      {
        property: "og:description",
        content: "Elige tu producto NFC, con QR según el modelo, y compra por WhatsApp.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://pymevisible.cl/productos" },
    ],
    links: [{ rel: "canonical", href: "https://pymevisible.cl/productos" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Productos NFC + QR de Pyme Visible",
          itemListElement: productos.map((producto, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `https://pymevisible.cl/productos/${producto.id}`,
            name: producto.name,
          })),
        }),
      },
    ],
  }),
});

function ProductosPage() {
  const search = Route.useSearch();
  const [categoria, setCategoria] = useState<CategoriaFiltro>(search.categoria ?? "todos");
  const [disponibilidad, setDisponibilidad] = useState<DisponibilidadFiltro>("todos");
  const [precio, setPrecio] = useState<PrecioFiltro>("todos");
  const [orden, setOrden] = useState<OrdenFiltro>("destacados");
  const tituloCategoria =
    categoria === "todos"
      ? "Todos los productos"
      : (CATEGORIAS_PRODUCTO.find((item) => item.id === categoria)?.label ?? "Todos los productos");

  const productosFiltrados = useMemo(() => {
    const resultado = productos.filter((producto) => {
      const coincideCategoria = categoria === "todos" || producto.category === categoria;
      const coincideDisponibilidad =
        disponibilidad === "todos" || producto.availability === disponibilidad;
      const coincidePrecio =
        precio === "todos" ||
        (precio === "hasta-10000" && producto.priceValue <= 10000) ||
        (precio === "10001-20000" && producto.priceValue > 10000 && producto.priceValue <= 20000) ||
        (precio === "mas-20000" && producto.priceValue > 20000);

      return coincideCategoria && coincideDisponibilidad && coincidePrecio;
    });

    return [...resultado].sort((a, b) => {
      if (orden === "precio-asc") return a.priceValue - b.priceValue;
      if (orden === "precio-desc") return b.priceValue - a.priceValue;
      if (orden === "nombre") return a.name.localeCompare(b.name, "es");
      return Number(Boolean(b.featured)) - Number(Boolean(a.featured));
    });
  }, [categoria, disponibilidad, precio, orden]);

  return (
    <div className="min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main id="contenido">
        <section className="pv-page-hero border-b border-ink/10">
          <div className="mx-auto max-w-[1320px] px-5 pb-4 pt-4 sm:px-8 sm:pb-9 sm:pt-10 lg:px-10">
            <a
              href="/"
              className="inline-flex min-h-9 items-center gap-2 text-xs font-semibold text-slate transition-colors hover:text-ink sm:min-h-11 sm:text-sm"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Volver a Pyme Visible
            </a>
            <div className="mt-2 sm:mt-4">
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
                Catálogo NFC + QR
              </p>
              <h1 className="pv-section-title mt-2 max-w-4xl sm:mt-3">
                Productos listos para conectar.
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate sm:mt-4 sm:text-base">
                Revisa stands NFC + QR, tarjetas NFC y llaveros para conectar a tus clientes con
                Google, WhatsApp, Instagram o TikTok.
              </p>
            </div>
            <div className="mt-5 hidden flex-wrap gap-x-6 gap-y-3 text-sm text-slate sm:flex">
              <span className="inline-flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" /> NFC configurado
              </span>
              <span className="inline-flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" /> QR incluido
              </span>
              <span className="inline-flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" /> Enviamos a todo Chile
              </span>
              <a href="/stand-nfc-qr" className="font-semibold text-primary hover:underline">
                Conoce nuestros stands NFC
              </a>
            </div>
          </div>
        </section>

        <section aria-labelledby="catalogo-titulo" className="pv-catalog-section">
          <div className="mx-auto max-w-[1320px] px-5 pb-8 pt-5 sm:px-8 sm:pb-12 sm:pt-9 lg:px-10">
            <div className="flex flex-col gap-3 border-b border-ink/10 pb-4 sm:gap-4 sm:pb-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">
                    Tienda
                  </p>
                  <h2
                    id="catalogo-titulo"
                    className="mt-2 text-3xl font-extrabold uppercase leading-none text-ink sm:text-5xl"
                  >
                    {tituloCategoria}
                  </h2>
                </div>
                <span className="hidden rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate sm:inline-flex">
                  {productosFiltrados.length}{" "}
                  {productosFiltrados.length === 1 ? "producto" : "productos"}
                </span>
              </div>

              <Tabs
                value={categoria}
                onValueChange={(value) => setCategoria(value as CategoriaFiltro)}
                className="w-full"
              >
                <TabsList className="pv-tabs-scroll h-auto w-full justify-start gap-2 overflow-x-auto rounded-none bg-transparent p-0 pb-1">
                  <TabsTrigger
                    value="todos"
                    className="pv-filter-pill min-h-9 rounded-full border border-ink/10 bg-white px-3 text-xs data-[state=active]:border-ink data-[state=active]:bg-ink data-[state=active]:text-white data-[state=active]:shadow-none sm:min-h-11 sm:px-5 sm:text-sm"
                  >
                    Todos
                  </TabsTrigger>
                  {CATEGORIAS_PRODUCTO.map((item) => (
                    <TabsTrigger
                      key={item.id}
                      value={item.id}
                      className="pv-filter-pill min-h-9 rounded-full border border-ink/10 bg-white px-3 text-xs data-[state=active]:border-ink data-[state=active]:bg-ink data-[state=active]:text-white data-[state=active]:shadow-none sm:min-h-11 sm:px-5 sm:text-sm"
                    >
                      {item.shortLabel}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              <div className="hidden gap-3 sm:grid sm:grid-cols-2 lg:grid-cols-[auto_14rem_14rem_15rem] lg:items-center">
                <span className="inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.14em] text-slate">
                  <SlidersHorizontal className="h-4 w-4" aria-hidden="true" /> Filtrar y ordenar
                </span>
                <Select
                  value={disponibilidad}
                  onValueChange={(value) => setDisponibilidad(value as DisponibilidadFiltro)}
                >
                  <SelectTrigger className="pv-filter-control h-12 rounded-xl border-ink/10 px-4">
                    <SelectValue placeholder="Disponibilidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Toda disponibilidad</SelectItem>
                    <SelectItem value="disponible">Disponible</SelectItem>
                    <SelectItem value="a-pedido">Hecho a pedido</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={precio} onValueChange={(value) => setPrecio(value as PrecioFiltro)}>
                  <SelectTrigger className="pv-filter-control h-12 rounded-xl border-ink/10 px-4">
                    <SelectValue placeholder="Precio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los precios</SelectItem>
                    <SelectItem value="hasta-10000">Hasta $10.000</SelectItem>
                    <SelectItem value="10001-20000">$10.001 a $20.000</SelectItem>
                    <SelectItem value="mas-20000">Más de $20.000</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={orden} onValueChange={(value) => setOrden(value as OrdenFiltro)}>
                  <SelectTrigger className="pv-filter-control h-12 rounded-xl border-ink/10 px-4">
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="destacados">Orden: destacados</SelectItem>
                    <SelectItem value="precio-asc">Precio: menor a mayor</SelectItem>
                    <SelectItem value="precio-desc">Precio: mayor a menor</SelectItem>
                    <SelectItem value="nombre">Nombre: A a Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {productosFiltrados.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-7 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
                {productosFiltrados.map((producto) => (
                  <CatalogoCard key={producto.id} producto={producto} />
                ))}
              </div>
            ) : (
              <div className="pv-surface-card mt-7 grid min-h-72 place-items-center rounded-[2rem] p-8 text-center">
                <div>
                  <PackageCheck className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
                  <h3 className="mt-4 text-3xl font-extrabold uppercase text-ink">
                    No hay productos con estos filtros
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setCategoria("todos");
                      setDisponibilidad("todos");
                      setPrecio("todos");
                    }}
                    className="pv-button pv-button--primary mt-5 min-h-11 px-5 py-3 text-sm"
                  >
                    Ver todo el catálogo
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
